import * as React from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'

import SearchInput from '../SearchInput'
import AccountMenu from '../AccountMenu'
import AppName from './AppName'
import { useFindStore, SEARCH_PHASES } from '../../stores/useFindStore'

/**
 * AppBar layout variant for `/:user/find`.
 *
 * Bypasses the dual-Grid 50/50 split that other views use because the
 * SearchInput needs most of the toolbar width. Bypasses `<PageTitle>` for
 * the same reason (its inner `flexGrow:1` would steal space) and to drop
 * the share button — search graphs aren't shareable as persistent URLs.
 *
 * The `pr: 19` (~152px) reserve clears the legacy `#customizationCaption`
 * link, which the EJS shell absolute-positions over the AppBar at z=10000.
 * Without it the result counter renders behind that link.
 *
 * When `searchPhase` is IDLE, the SearchInput lives in the hero block
 * (see SearchEmptyStates.IdleState) so the user lands on a centered prompt.
 * As soon as a search starts, the input re-mounts here and framer-motion's
 * shared `layoutId` slides it up into the toolbar.
 */
export default function SearchHeader({ pageLabel, onAccountMenuClick }) {
    const searchPhase = useFindStore(s => s.searchPhase)
    const showSearchInput = searchPhase !== SEARCH_PHASES.IDLE

    return (
        <>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    // Page label is hidden below `md`, so no inter-child gap is
                    // needed there; that frees the full toolbar for the input.
                    gap: { xs: 0, md: 10 },
                    pl: { xs: 1, md: 3 },
                    // `#customizationCaption` (legacy absolute link at right:120px)
                    // is `display:none` below 450px, so we can pull the right
                    // padding way in on phones to give the pill more room.
                    pr: { xs: 2, md: 19 },
                }}
            >
                <Typography
                    variant="h6"
                    noWrap
                    sx={{
                        color: theme => theme.palette.text.primary,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        display: { xs: 'none', md: 'block' },
                    }}
                >
                    {pageLabel}
                </Typography>
                {showSearchInput && (
                    // Layout-transparent wrapper: mirrors the flex grow + min-width
                    // that `SearchInput`'s own root used to apply directly to the
                    // toolbar, so the AppBar pill renders identically to before.
                    <motion.div
                        layoutId="graph-search-input"
                        transition={{
                            type: 'spring',
                            stiffness: 320,
                            damping: 32,
                        }}
                        style={{
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <SearchInput />
                    </motion.div>
                )}
            </Box>
            <Box
                sx={{
                    flexShrink: 0,
                    pr: 1,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <AppName />
                <AccountMenu onAccountMenuClick={onAccountMenuClick} />
            </Box>
        </>
    )
}
